import React, { useState, useCallback } from "react";
import { DrawingForm } from "./DrawingForm";
import { DrawingPreview } from "./DrawingPreview";
import { ConversionResult } from "./ConversionResult";
import LoadingSpinner  from "./LoadingSpinner";
import { drawingApi } from "../../../api/drawingApi";
import {
  DrawingConversionRequest,
  DrawingConversionResponse,
  getFileExtension,
  isValidInputFormat,
} from "../../../types/drawingTypes";

/**
 * 図面変換コンポーネント
 * @description 図面ファイルの変換とプレビュー表示を管理するメインコンポーネント
 */
export const DrawingConverter: React.FC = () => {
  //#region 状態管理
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<DrawingConversionResponse | null>(null);
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [previewError, setPreviewError] = useState<string | undefined>();
  const [progress, setProgress] = useState<string>("");
  const [previewSvgData, setPreviewSvgData] = useState<string | null>(null);
  //#endregion

  //#region 一時ファイル削除処理
  const deleteTempFile = useCallback(async (filePath: string) => {
    try {
      await drawingApi.deleteFile(filePath);
      console.info("一時ファイル削除完了:", filePath);
    } catch (error) {
      console.error("ファイル削除エラー:", error);
    }
  }, []);
  //#endregion

  //#region プレビュー生成処理
  const generatePreview = useCallback(async (filePath: string) => {
    try {
      setProgress("プレビューを生成中...");
      setPreviewError(undefined);

      const previewRequest: DrawingConversionRequest = {
        conversionParameters: {
          inputFilePath: filePath,
          outputFormat: "svg",
          outputFilePath: `${filePath}_preview.svg`,
          quality: 90,
        },
      };

      const previewResult = await drawingApi.convertFile(previewRequest);

      if (previewResult.success && previewResult.convertedFilePath) {
        const svgBlob = await drawingApi.downloadFile(previewResult.convertedFilePath);
        const svgText = await svgBlob.text();
        setPreviewSvgData(svgText);
        await deleteTempFile(previewResult.convertedFilePath);
      }
    } catch (error) {
      console.error("プレビュー生成エラー:", error);
      setPreviewError(error instanceof Error ? error.message : "プレビューの生成に失敗しました");
      setPreviewSvgData(null);
    }
  }, [deleteTempFile]);
  //#endregion

  //#region 状態リセット処理
  const handleReset = useCallback(() => {
    setResult(null);
    setInputFile(null);
    setProgress("");
    setPreviewError(undefined);
    setPreviewSvgData(null);
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  }, []);
  //#endregion

  //#region ファイル選択処理
  const handleFileSelect = useCallback(async (file: File | null) => {
    setResult(null);
    setInputFile(file);
    setProgress("");
    setPreviewError(undefined);
    setPreviewSvgData(null);

    if (file) {
      const extension = getFileExtension(file.name);
      if (!isValidInputFormat(extension)) {
        setPreviewError("サポートされていないファイル形式です");
        return;
      }

      let uploadedFilePath: string | null = null;
      try {
        setIsLoading(true);
        setProgress("ファイルをアップロード中...");

        const uploadResult = await drawingApi.uploadFile(file);
        if (!uploadResult.success) {
          throw new Error("ファイルのアップロードに失敗しました");
        }
        uploadedFilePath = uploadResult.filePath;

        await generatePreview(uploadResult.filePath);

        if (uploadedFilePath) {
          await deleteTempFile(uploadedFilePath);
        }
      } catch (error) {
        console.error("ファイル処理エラー:", error);
        setPreviewError(error instanceof Error ? error.message : "ファイル処理中にエラーが発生しました");
        if (uploadedFilePath) {
          await deleteTempFile(uploadedFilePath);
        }
      } finally {
        setIsLoading(false);
        setProgress("");
      }
    }
  }, [generatePreview, deleteTempFile]);
  //#endregion

  //#region ファイル変換処理
  const handleConversion = useCallback(async (request: DrawingConversionRequest) => {
    let uploadedFilePath: string | null = null;

    try {
      setIsLoading(true);
      setResult(null);
      setProgress("ファイルをアップロード中...");

      const uploadResult = await drawingApi.uploadFile(inputFile!);
      uploadedFilePath = uploadResult.filePath;

      setProgress("ファイルを変換中...");

      const conversionRequest: DrawingConversionRequest = {
        conversionParameters: {
          ...request.conversionParameters,
          inputFilePath: uploadResult.filePath,
        },
      };
      const conversionResult = await drawingApi.convertFile(conversionRequest);

      if (conversionResult.success && conversionResult.convertedFilePath) {
        setProgress("変換完了。ファイルをダウンロード中...");

        const fileBlob = await drawingApi.downloadFile(conversionResult.convertedFilePath);
        const fileName = conversionResult.convertedFilePath.split("\\").pop() || "converted.file";

        const downloadUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        if (uploadedFilePath) {
          await deleteTempFile(uploadedFilePath);
        }
        if (conversionResult.convertedFilePath) {
          await deleteTempFile(conversionResult.convertedFilePath);
        }

        setProgress("処理が完了しました");
      }

      setResult(conversionResult);
    } catch (error) {
      console.error("変換エラー:", error);
      setResult({
        success: false,
        message: "変換処理中にエラーが発生しました",
        convertedFilePath: null,
        errorDetails: error instanceof Error ? error.message : "不明なエラー",
      });

      if (uploadedFilePath) {
        await deleteTempFile(uploadedFilePath);
      }
    } finally {
      setIsLoading(false);
    }
  }, [inputFile, deleteTempFile]);
  //#endregion

  //#region UIレンダリング
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-full mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <div className="lg:col-span-3 order-1">
            <div className="sticky top-4 space-y-4">
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                <DrawingForm
                  onSubmit={handleConversion}
                  onFileSelect={handleFileSelect}
                  onReset={handleReset}
                  isLoading={isLoading}
                />
              </div>
              {result && (
                <div className="bg-white rounded-lg shadow-sm">
                  <ConversionResult result={result} />
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-9 order-2">
            <div className="bg-white rounded-lg shadow-sm h-[calc(100vh-120px)]">
              <DrawingPreview
                file={inputFile}
                isLoading={isLoading}
                error={previewError}
                svgData={previewSvgData}
              />
            </div>
          </div>
        </div>

        {progress && (
          <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">{progress}</p>
          </div>
        )}
      </div>

      {isLoading && <LoadingSpinner />}
    </div>
  );
  //#endregion
};

export default DrawingConverter;